import axios from 'axios';
import React, { useCallback, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { EditMyInfoProps, UserInfo } from '.';
import { Header } from './Header';
import { CloseIcon } from './Icon';
import { LabelInput, LabelSelect, SelectOption } from './LabelInput';
import { AlertContext } from '../App';
import { validateCustomerAge, validateCustomerBirthday, validateCustomerName } from '../datautils/customer';
import { getErrorMessage } from '../util/message';
import { makeRich } from '../util/richText';

import style from './Info.module.scss';
import contentStyle from './EditMyInfoComponents.module.scss';
import { setDayLimitFromMonth } from 'datautils/dateutil';
import { useTranslation } from 'react-i18next';
import FooterBanner from './FooterBanner';

interface Status {
  error: boolean;
  done: boolean;
}

export interface BannerProps {
  label: string;
  image: string;
  link: string;
}

interface ContentProps {
  accessToken: string;
  userInfo: UserInfo;
  goEditPhoneNumber(): void;
  onChangeBirthday(val: string): void;
  onChangeAge(val: string): void;
  onChangeGender(val: 'female' | 'male'): void;
  onChangeName(val: string): void;
  onChangePhoneNumber(val: string): void;
  onFailed(contents: string): void;
}

const Content: React.FC<ContentProps> = (props: ContentProps) => {
  const {
    accessToken,
    goEditPhoneNumber,
    onChangeBirthday,
    onChangeAge,
    onChangeGender,
    onChangeName,
    onFailed,
    userInfo,
  } = props;
  const { t, i18n } = useTranslation();
  const defaultStatus: Status = { error: false, done: false };
  const [birthdayStatus, setBirthdayStatus] = useState(defaultStatus);
  const [ageStatus, setAgeStatus] = useState(defaultStatus);
  const [nameStatus, setNameStatus] = useState(defaultStatus);
  const [genderStatus, setGenderStatus] = useState(defaultStatus);
  const [age, setAge] = useState(userInfo.age);
  const [name, setName] = useState(userInfo.name);
  const [birthday, setBirthday] = useState(userInfo.birthdaymmdd);
  const config = {
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: { Authorization: `Bearer ${accessToken}` },
  };

  const handleBirthdayChange = async (e: React.ChangeEvent<HTMLDataElement>) => {
    let val = e.currentTarget.value;

    // MM-DD 이상 입력하면 입력 무시
    if (val.length > 5) {
      return;
    }

    const month = getMonthSlice(val);
    const day = getDaySlice(val);

    const composite: string[] = [];

    // 입력값을 [MM,DD]로 분리
    function processTwoDigitInput(input: string, limit: string) {
      switch (input.length) {
        case 0:
          break;
        case 1:
          if (parseInt(input, 10) <= parseInt(limit.charAt(0), 10)) composite.push(input);
          else composite.push('0' + input);
          break;
        case 2:
          if (parseInt(input, 10) <= parseInt(limit, 10)) composite.push(input);
          else composite.push(input.charAt(0));
          break;
      }
    }

    const MONTH_LIMIT = '12';
    const DAY_LIMIT = setDayLimitFromMonth(month);

    processTwoDigitInput(month, MONTH_LIMIT);
    processTwoDigitInput(day, DAY_LIMIT);

    setBirthday(composite.join('-'));
  };

  const handleBirthdayChangeDone = async () => {
    const validate = validateCustomerBirthday(birthday || '');
    if (validate[0]) {
      try {
        const data = { birthdaymmdd: birthday };
        const resp = await axios.put(`/mobile/users/${userInfo.uuid}/`, data, config);
        onChangeBirthday(resp.data.user.birthdaymmdd);
        setBirthdayStatus({ error: false, done: true });
        const timer = window.setTimeout(() => {
          setBirthdayStatus(defaultStatus);
          window.clearTimeout(timer);
        }, 2000);
      } catch (err) {
        setBirthdayStatus({ error: true, done: false });
        onFailed(getErrorMessage(err));
      }
    } else {
      setBirthdayStatus({ error: true, done: false });
      onFailed(validate[1]);
    }
  };
  const handleAgeChange = (e: React.ChangeEvent<HTMLDataElement>) => {
    const val = e.currentTarget.value;
    setAge(Number(val));
  };
  const handleAgeChangeDone = async () => {
    const parsedAge = age ? age.toString() : '';
    const validate = validateCustomerAge(parsedAge, userInfo.country);
    if (validate[0]) {
      try {
        const data = { age };
        const resp = await axios.put(`/mobile/users/${userInfo.uuid}/`, data, config);
        onChangeAge(resp.data.user.age);
        setAgeStatus({ error: false, done: true });
        const timer = window.setTimeout(() => {
          setAgeStatus(defaultStatus);
          window.clearTimeout(timer);
        }, 2000);
      } catch (err) {
        setAgeStatus({ error: true, done: false });
        onFailed(getErrorMessage(err));
      }
    } else {
      setAgeStatus({ error: true, done: false });
      onFailed(validate[1]);
    }
  };
  const handleNameChange = (e: React.ChangeEvent<HTMLDataElement>) => {
    const val = e.currentTarget.value;
    setName(val);
  };
  const handleNameChangeDone = async () => {
    const validate = validateCustomerName(name || '');
    if (validate[0]) {
      try {
        const data = { name };
        const resp = await axios.put(`/mobile/users/${userInfo.uuid}/`, data, config);
        onChangeName(resp.data.user.name);
        setNameStatus({ error: false, done: true });
        const timer = window.setTimeout(() => {
          setNameStatus(defaultStatus);
          window.clearTimeout(timer);
        }, 2000);
      } catch (err) {
        setNameStatus({ error: true, done: false });
        onFailed(getErrorMessage(err));
      }
    } else {
      setNameStatus({ error: true, done: false });
      onFailed(validate[1]);
    }
  };
  const handleGenderChange = async (e: React.ChangeEvent<HTMLDataElement>) => {
    const gender = e.currentTarget.value === 'female' ? 'female' : 'male';
    try {
      const data = { gender: gender };
      await axios.put(`/mobile/users/${userInfo.uuid}/`, data, config);
      onChangeGender(gender);
      setGenderStatus({ error: false, done: true });
      const timer = window.setTimeout(() => {
        setGenderStatus(defaultStatus);
        window.clearTimeout(timer);
      }, 2000);
    } catch (err) {
      onFailed(getErrorMessage(err));
    }
  };
  const handleFocusBithday = () => {
    setAgeStatus(defaultStatus);
    setNameStatus(defaultStatus);
  };
  const handleFocusAge = () => {
    setNameStatus(defaultStatus);
  };
  const handleFocusGender = () => {
    setAgeStatus(defaultStatus);
    setNameStatus(defaultStatus);
  };
  const handleFocusName = () => {
    setAgeStatus(defaultStatus);
  };
  const handleEditPhoneNumber = () => {
    goEditPhoneNumber();
  };

  const changeButton = (
    // eslint-disable-next-line
    <a className={contentStyle.change} onClick={handleEditPhoneNumber} data-id="btn_edit_phone_number">
      {userInfo.phoneNumber ? i18n.t('변경') : i18n.t('입력')}
    </a>
  );

  const options: SelectOption[] = [
    { id: 0, name: i18n.t('여성'), value: 'female' },
    { id: 1, name: i18n.t('남성'), value: 'male' },
  ];

  function getMonthSlice(str: string) {
    return str.substring(0, 2);
  }

  function getDaySlice(str: string) {
    const formattedString = str.replace('-', '');
    return formattedString.substring(2, 4);
  }

  return (
    <div className={contentStyle.container}>
      <div className={contentStyle.wrapper}>
        <div className={contentStyle.cards}>
          <LabelInput
            type="text"
            label={t('전화번호')}
            placeholder="01012345678"
            value={userInfo.phoneNumber}
            disabled={true}
            children={changeButton}
          />
          <LabelInput
            dataId="input_birthday"
            type="text"
            label={t('생일')}
            placeholder={t('생일을 입력하세요')}
            value={birthday}
            error={birthdayStatus.error}
            done={birthdayStatus.done}
            onChange={handleBirthdayChange}
            onBlur={handleBirthdayChangeDone}
            onFocus={handleFocusBithday}
          />
          <LabelInput
            dataId="input_age"
            type="number"
            label={t('나이(만)')}
            placeholder={t('나이(만) 입력하세요')}
            value={age ? age.toString() : ''}
            error={ageStatus.error}
            done={ageStatus.done}
            onChange={handleAgeChange}
            onBlur={handleAgeChangeDone}
            onFocus={handleFocusAge}
          />
          <LabelInput
            dataId="input_name"
            type="text"
            label={t('이름')}
            placeholder={t('이름을 입력하세요')}
            value={name}
            error={nameStatus.error}
            done={nameStatus.done}
            onChange={handleNameChange}
            onBlur={handleNameChangeDone}
            onFocus={handleFocusName}
          />
          <LabelSelect
            dataId="select_gender"
            label={t('성별')}
            placeholder={t('성별을 입력하세요')}
            options={options}
            value={userInfo.gender}
            done={genderStatus.done}
            onChange={handleGenderChange}
            onFocus={handleFocusGender}
          />
        </div>
        <p className={contentStyle.expireInfo}>
          {makeRich(t('개인정보 보호를 위해 해당 페이지는\n24시간 이후 만료됩니다.'))}
        </p>
      </div>

      <FooterBanner target="edit-my-info" userInfo={userInfo} />
    </div>
  );
};

type InfoProps = EditMyInfoProps;

export const Info: React.FC<InfoProps> = ({ accessToken, userInfo, handleUserChanges }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const onChange = (k: string) => {
    return (v: any) => {
      handleUserChanges && handleUserChanges({ ...userInfo, [k]: v });
    };
  };
  const alert = useContext(AlertContext);
  const handleFailure = useCallback(
    (message: string) => {
      alert({
        content: message,
        type: 'error',
      });
    },
    [alert]
  );
  const goEditPhoneNumber = () => navigate('/phone-number', { state: location.state });
  const goProfile = () => navigate('/profile', { state: location.state });

  const dodoLogo = (
    <div className={style.infoHeader}>
      <div className={style.infoHeaderLeft}>
        <h3>{t('내 정보를 관리하세요.')}</h3>
      </div>
      <button data-id="btn_close" className={style.btnClose} onClick={goProfile}>
        <CloseIcon />
      </button>
    </div>
  );
  return (
    <>
      <Header title={dodoLogo} />
      <Content
        accessToken={accessToken}
        userInfo={userInfo}
        goEditPhoneNumber={goEditPhoneNumber}
        onChangeBirthday={onChange('birthdaymmdd')}
        onChangeAge={onChange('age')}
        onChangeGender={onChange('gender')}
        onChangeName={onChange('name')}
        onChangePhoneNumber={onChange('phoneNumber')}
        onFailed={handleFailure}
      />
    </>
  );
};

export default Info;
